import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Lock } from "lucide-react";
import { SEO } from "./components";

const YEAR = new Date().getFullYear();

function LegalLayout({ title, icon: Icon, seo, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO {...seo} />
      <section className="pt-32 pb-16 px-6 lg:px-12" data-testid={`page-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-[#A89FA8] hover:text-[#F5D48A] text-xs tracking-widest uppercase mb-10">
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="flex items-center gap-4 mb-8">
            <Icon size={32} className="text-[#F5D48A]" />
            <div>
              <div className="section-label">Legal</div>
              <h1 className="display-serif text-4xl sm:text-5xl">{title}</h1>
            </div>
          </div>
          <div className="sf-card jewel-card jewel-gold rounded-sm p-8 md:p-12 space-y-6 text-[#F2EFE8]/85 leading-relaxed">
            {children}
          </div>
          <p className="text-xs text-[#A89FA8] mt-8 tracking-widest text-center">
            LAST UPDATED · {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>
    </>
  );
}

const H = ({ children }) => <h2 className="serif text-2xl text-[#F5D48A] mt-8 first:mt-0">{children}</h2>;

export function CopyrightPage() {
  return (
    <LegalLayout title="Copyright Notice" icon={Shield} seo={{
      title: "Copyright Notice | SnapshotFame",
      description: "Copyright and intellectual property notice for SnapshotFame — Life Clarity Chic by Dawn Delisle.",
    }}>
      <H>© {YEAR} SnapshotFame™ — Life Clarity Chic. All Rights Reserved.</H>
      <p>
        All content on this website — including but not limited to the SnapshotFame™ name and logo,
        the Life Clarity Chic brand, the Mirror Lotus 21-card deck and its accompanying guidebook,
        all Snapshot digital products, written material, photographs, artwork, card designs,
        session frameworks, video content, and site design — is the exclusive intellectual property of
        <strong className="text-[#F5D48A]"> Dawn Delisle </strong>
        and is protected under Canadian and international copyright, trademark, and intellectual property laws.
      </p>
      <H>What This Means</H>
      <p>
        You may not, without written permission from Dawn Delisle:
      </p>
      <ul className="list-disc list-inside space-y-2 text-[#F2EFE8]/80 pl-2">
        <li>Copy, reproduce, distribute, republish, or resell any part of this site or the digital products</li>
        <li>Modify, adapt, translate, or create derivative works from any Mirror Lotus card, guidebook page, or Snapshot worksheet</li>
        <li>Use the SnapshotFame™ name, key logo, or any Mirror Lotus artwork for commercial purposes</li>
        <li>Share, upload, forward, or distribute the digital guidebook, card images, or Snapshot PDFs to third parties</li>
        <li>Screen-record or transcribe live private clarity sessions for redistribution</li>
      </ul>
      <H>Personal Use Permitted</H>
      <p>
        When you purchase a Snapshot or the Mirror Lotus Deck & Guidebook, you receive a
        <em> personal, non-transferable, non-commercial license</em> to use the materials for your own
        self-reflection and spiritual growth. Copies are for you — not for redistribution.
      </p>
      <H>Report Unauthorized Use</H>
      <p>
        If you believe someone is using SnapshotFame™ material without authorization, please contact:
        <a href="mailto:divainkhair@gmail.com" className="text-[#F5D48A] underline ml-1">divainkhair@gmail.com</a>
      </p>
      <p className="text-sm text-[#A89FA8] pt-4 border-t border-[#B58B2E]/25">
        SnapshotFame™ and Life Clarity Chic™ are trademarks of Dawn Delisle.
      </p>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" icon={FileText} seo={{
      title: "Terms of Service | SnapshotFame",
      description: "Terms and conditions for using SnapshotFame — Life Clarity Chic.",
    }}>
      <H>Welcome</H>
      <p>
        These Terms of Service govern your use of the SnapshotFame™ website and any digital products or
        clarity sessions purchased from Dawn Delisle. By using this site or buying a product, you agree to
        these terms.
      </p>
      <H>1. Nature of Services</H>
      <p>
        SnapshotFame™ offers reflective, spiritual, and self-exploration experiences — Snapshot digital products,
        the Mirror Lotus deck & guidebook, and private Clarity/Truth sessions with Dawn Delisle.
      </p>
      <p>
        <strong className="text-[#F5D48A]">These services are for personal reflection and entertainment purposes.</strong>
        They are not, and are not intended to be, a substitute for professional medical, psychiatric,
        psychological, legal, or financial advice. Please consult qualified professionals for those matters.
      </p>
      <H>2. Purchases & Delivery</H>
      <p>
        All digital products (Snapshots, Mirror Lotus Deck & Guidebook) are delivered personally by Dawn via
        email after purchase. Once payment is received, please allow a reasonable time for personal delivery.
      </p>
      <p>
        Live sessions will be scheduled personally by Dawn after purchase via email. All sales are final unless
        otherwise stated.
      </p>
      <H>3. Refund Policy</H>
      <p>
        Because digital products are personally delivered and cannot be returned, and because live sessions involve
        Dawn's dedicated time, all sales are <strong>final</strong>. Refund requests will be considered on a
        case-by-case basis for undelivered digital items only.
      </p>
      <H>4. Intellectual Property</H>
      <p>
        See our full <Link to="/copyright" className="text-[#F5D48A] underline">Copyright Notice</Link>. All content
        is owned by Dawn Delisle and may not be reproduced without written permission.
      </p>
      <H>5. Your Conduct</H>
      <p>You agree to use this site respectfully and to not:</p>
      <ul className="list-disc list-inside space-y-2 text-[#F2EFE8]/80 pl-2">
        <li>Attempt to hack, disrupt, or gain unauthorized access to the site or admin area</li>
        <li>Redistribute or resell purchased materials</li>
        <li>Impersonate Dawn Delisle or the SnapshotFame™ brand</li>
      </ul>
      <H>6. Limitation of Liability</H>
      <p>
        SnapshotFame™ and Dawn Delisle are not liable for any decisions, actions, or outcomes you experience
        based on interactions with our materials or sessions. You are the author of your own life.
      </p>
      <H>7. Changes</H>
      <p>
        These terms may be updated over time. Continued use of the site indicates acceptance of any changes.
      </p>
      <H>8. Contact</H>
      <p>
        Questions? Reach Dawn at
        <a href="mailto:divainkhair@gmail.com" className="text-[#F5D48A] underline ml-1">divainkhair@gmail.com</a>
      </p>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" icon={Lock} seo={{
      title: "Privacy Policy | SnapshotFame",
      description: "How SnapshotFame — Life Clarity Chic collects and protects your personal information.",
    }}>
      <H>Your Privacy Matters</H>
      <p>
        SnapshotFame™ respects your privacy. This policy explains what we collect, why, and how we protect it.
      </p>
      <H>What We Collect</H>
      <p>When you buy a product, book a session, or sign up for The Friday Mirror, we collect:</p>
      <ul className="list-disc list-inside space-y-2 text-[#F2EFE8]/80 pl-2">
        <li>Your name (only if you provide it at checkout)</li>
        <li>Your email address</li>
        <li>Which product or session you're interested in</li>
        <li>The date and time of your interaction</li>
      </ul>
      <p>
        We do <strong>not</strong> collect payment card details on our site — all payments are processed by
        secure third-party providers (such as PayPal). We do not see or store your card information.
      </p>
      <H>How We Use It</H>
      <p>Your information is used only to:</p>
      <ul className="list-disc list-inside space-y-2 text-[#F2EFE8]/80 pl-2">
        <li>Deliver the digital product or session you purchased</li>
        <li>Send you the Friday Mirror email if you signed up</li>
        <li>Communicate with you personally if you have a question</li>
      </ul>
      <p>
        <strong className="text-[#F5D48A]">We will never sell, rent, or share your email or personal
        information with anyone.</strong>
      </p>
      <H>Cookies & Analytics</H>
      <p>
        This site may use privacy-friendly analytics (Google Analytics, Meta Pixel) to understand how visitors
        interact with the site so we can improve the experience. These tools may set cookies. You can disable
        cookies in your browser settings.
      </p>
      <H>Unsubscribe Any Time</H>
      <p>
        Every email from the Friday Mirror includes an unsubscribe option. You may also email Dawn directly to
        be removed from any list.
      </p>
      <H>Your Rights</H>
      <p>
        You have the right to request access to, correction of, or deletion of your personal information at any
        time. Contact <a href="mailto:divainkhair@gmail.com" className="text-[#F5D48A] underline">divainkhair@gmail.com</a>.
      </p>
      <H>Data Protection</H>
      <p>
        Your data is stored in a secure encrypted database. We take reasonable technical measures to protect it,
        but no method of transmission over the Internet is 100% secure. We hold your trust with the same care
        you'd expect from someone who reads every message personally.
      </p>
    </LegalLayout>
  );
}
